/**
 * ActivitiesTabs - Sistema de abas para filtrar atividades por tipo
 * Substitui o dropdown Select por abas horizontais com indicador sublinhado
 * Exibe badges com contadores de atividades pendentes por tipo
 * 
 * @version 1.1.0
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 * RELEASE: 2026-02-09 v1.1.0 - Adicionado badges de contadores por tipo
 */
import React from 'react';
import { FileText, Phone, Mail, Calendar, CheckSquare, MessageSquare, Cog } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ActivityType } from '@/hooks/useCrmDealActivities';

/**
 * Tipo das abas disponíveis no feed
 */
export type ActivityFeedTab = 'all' | ActivityType | 'comments' | 'system';

/**
 * Interface dos contadores por aba
 */
export interface ActivityTabCounts {
  all?: number;
  note?: number;
  call?: number;
  email?: number;
  meeting?: number;
  task?: number;
  system?: number;
  comments?: number;
}

/**
 * Props do componente de abas
 */
interface ActivitiesTabsProps {
  activeTab: ActivityFeedTab;
  onTabChange: (tab: ActivityFeedTab) => void;
  counts?: ActivityTabCounts;
  isLoadingCounts?: boolean;
}

/**
 * Configuração das abas
 */
const tabsConfig: { id: ActivityFeedTab; label: string; icon: React.ElementType | null }[] = [
  { id: 'all', label: 'Todos', icon: null },
  { id: 'note', label: 'Nota', icon: FileText },
  { id: 'call', label: 'Ligação', icon: Phone },
  { id: 'email', label: 'E-mail', icon: Mail },
  { id: 'meeting', label: 'Reunião', icon: Calendar },
  { id: 'task', label: 'Lembrete', icon: CheckSquare },
  { id: 'comments', label: 'Comentários', icon: MessageSquare },
  { id: 'system', label: 'Sistema', icon: Cog },
];

/**
 * Componente de abas horizontais para filtrar atividades
 */
export function ActivitiesTabs({ activeTab, onTabChange, counts, isLoadingCounts }: ActivitiesTabsProps) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto border-b border-border pb-px">
      {tabsConfig.map((tab, index) => {
        const isActive = activeTab === tab.id;
        const Icon = tab.icon;
        const showSeparator = tab.id === 'all';
        
        // Obtém o contador para esta aba
        const count = counts?.[tab.id as keyof ActivityTabCounts];
        const hasCount = count !== undefined && count > 0;

        return (
          <React.Fragment key={tab.id}>
            <button
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={cn(
                "relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap",
                "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isActive 
                  ? "text-foreground" 
                  : "text-muted-foreground"
              )}
            >
              {/* Ícone da aba */}
              {Icon && <Icon className="h-4 w-4" />}
              
              {/* Label da aba */}
              <span>{tab.label}</span>
              
              {/* Badge de contagem para todas as abas com contador > 0 */}
              {hasCount && !isLoadingCounts && (
                <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-medium text-primary">
                  {count > 99 ? '99+' : count}
                </span>
              )}
              
              {/* Indicador de aba ativa (sublinhado) */}
              {isActive && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
            
            {/* Separador vertical após "Todos" */}
            {showSeparator && (
              <div className="h-5 w-px bg-border mx-1" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}